import React, { useEffect, useState } from 'react';
import GatewayV2Services from '../../services/externalServices/TicketServices/gatewayV2Services';
import TheatreServices from '../../services/externalServices/TicketServices/theatreServices';
import PricingConfigServices from '../../services/externalServices/TicketServices/pricingConfigServices';

const emptyForm = {
  serviceType: 'Tickets',
  serviceId: '',
  profileCode: '',
  active: true,
  credentials: '{"terminalCodeRef":"","passwordRef":""}',
  returnUrls: '{"webSuccess":"","webCancel":"","webDecline":"","nativeDeeplink":""}',
  confirmUrl: '',
  cancelUrl: ''
};

export default function Accounts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [theatres, setTheatres] = useState([]);
  const [theatresLoading, setTheatresLoading] = useState(false);
  const [gatewayMaster, setGatewayMaster] = useState([]);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await GatewayV2Services.listGatewayAccounts();
      setItems(res?.data || res?.items || []);
    } catch (e) {
      setError('Failed to load accounts');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    // Preload theatres for Tickets serviceType selection
    const fetch = async () => {
      setTheatresLoading(true);
      try {
        const res = await TheatreServices.listTheatres();
        const list = Array.isArray(res?.data) ? res.data : (Array.isArray(res) ? res : []);
        setTheatres(list);
      } catch (_) { setTheatres([]); }
      finally { setTheatresLoading(false); }
    };
    fetch();
  }, []);
  useEffect(() => {
    (async () => {
      try {
        const gm = await PricingConfigServices.listGatewaysMaster();
        const list = gm?.data || gm || [];
        setGatewayMaster(Array.isArray(list) ? list : []);
      } catch (_) { setGatewayMaster([]); }
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      // Validate JSON editors
      const invalidJsonEntered = (txt) => (txt && txt.trim() !== '' && txt.trim() !== '{}' && txt.trim() !== '[]');
      const parsedCreds = safeJson(form.credentials);
      const parsedUrls = safeJson(form.returnUrls);
      if (invalidJsonEntered(form.credentials) && Object.keys(parsedCreds).length === 0) {
        setError('Credentials JSON is invalid.');
        return;
      }
      if (invalidJsonEntered(form.returnUrls) && Object.keys(parsedUrls).length === 0) {
        setError('Return URLs JSON is invalid.');
        return;
      }
      const payload = {
        serviceType: form.serviceType,
        serviceId: form.serviceId.trim(),
        profileCode: form.profileCode.trim(),
        active: !!form.active,
        credentials: parsedCreds,
        returnUrls: parsedUrls,
        confirmUrl: form.confirmUrl || undefined,
        cancelUrl: form.cancelUrl || undefined,
      };
      if (editingId) await GatewayV2Services.updateGatewayAccount(editingId, payload);
      else await GatewayV2Services.createGatewayAccount(payload);
      setForm(emptyForm); setEditingId(null);
      load();
    } catch (e) {
      setError(e?.message || 'Save failed');
    }
  };

  const safeJson = (txt) => { try { return JSON.parse(txt || '{}'); } catch { return {}; } };

  const startEdit = (it) => {
    setEditingId(it._id);
    setForm({
      serviceType: it.serviceType || 'Tickets',
      serviceId: it.serviceId || '',
      profileCode: it.profileCode || '',
      active: it.active !== false,
      credentials: JSON.stringify(it.credentials || {}, null, 2),
      returnUrls: JSON.stringify(it.returnUrls || {}, null, 2),
      confirmUrl: it.confirmUrl || '',
      cancelUrl: it.cancelUrl || ''
    });
  };

  const resetForm = () => { setForm(emptyForm); setEditingId(null); setError(''); };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">Payments v2 — Gateway Accounts</h2>
      {error && <div className="mb-3 text-red-600">{error}</div>}

      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm">Service Type</label>
          <select className="border rounded px-3 py-2 w-full" value={form.serviceType} onChange={e=>setForm({...form, serviceType:e.target.value})}>
            {['Tickets','TourPackage','Taxi','Auto'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        {form.serviceType === 'Tickets' ? (
          <div>
            <label className="block text-sm">Theatre</label>
            <select className="border rounded px-3 py-2 w-full" value={form.serviceId} onChange={e=>setForm({...form, serviceId:e.target.value})}>
              <option value="">Select theatre</option>
              {theatres.map(th => (
                <option key={th._id} value={th._id}>{th.name}</option>
              ))}
            </select>
            {theatresLoading && <div className="text-xs text-gray-500 mt-1">Loading theatres...</div>}
          </div>
        ) : (
          <div>
            <label className="block text-sm">Service ID</label>
            <input className="border rounded px-3 py-2 w-full" value={form.serviceId} onChange={e=>setForm({...form, serviceId:e.target.value})} placeholder="e.g., vendorId" />
          </div>
        )}
        <div>
          <label className="block text-sm">Gateway Profile</label>
          {gatewayMaster.length > 0 ? (
            <select className="border rounded px-3 py-2 w-full" value={form.profileCode} onChange={e=>setForm({...form, profileCode:e.target.value})}>
              <option value="">Select gateway</option>
              {gatewayMaster.filter(g=>g.active!==false).map(g => (
                <option key={g._id} value={g.code}>{g.name} ({g.code})</option>
              ))}
            </select>
          ) : (
            <input className="border rounded px-3 py-2 w-full" value={form.profileCode} onChange={e=>setForm({...form, profileCode:e.target.value})} />
          )}
        </div>
        <div className="flex items-center gap-2">
          <input type="checkbox" checked={!!form.active} onChange={e=>setForm({...form, active:e.target.checked})} />
          <span>Active</span>
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm">Credentials (env refs JSON)</label>
          <textarea className="border rounded px-3 py-2 w-full font-mono" rows={4} value={form.credentials} onChange={e=>setForm({...form, credentials:e.target.value})} />
        </div>
        <div className="md:col-span-2">
          <label className="block text-sm">Return URLs (JSON)</label>
          <textarea className="border rounded px-3 py-2 w-full font-mono" rows={3} value={form.returnUrls} onChange={e=>setForm({...form, returnUrls:e.target.value})} />
        </div>
        <div>
          <label className="block text-sm">Confirm URL (optional)</label>
          <input className="border rounded px-3 py-2 w-full" value={form.confirmUrl} onChange={e=>setForm({...form, confirmUrl:e.target.value})} />
        </div>
        <div>
          <label className="block text-sm">Cancel URL (optional)</label>
          <input className="border rounded px-3 py-2 w-full" value={form.cancelUrl} onChange={e=>setForm({...form, cancelUrl:e.target.value})} />
        </div>
        <div className="md:col-span-2 flex gap-3">
          <button type="submit" className="px-4 py-2 bg-black text-white rounded">{editingId? 'Update' : 'Create'}</button>
          <button type="button" className="px-4 py-2 bg-gray-200 rounded" onClick={resetForm}>Reset</button>
        </div>
      </form>

      <div>
        <h3 className="text-xl font-medium mb-2">Existing Accounts</h3>
        {loading ? <div>Loading...</div> : (
          <table className="min-w-full border">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 py-1 border">Service</th>
                <th className="px-2 py-1 border">Service Target</th>
                <th className="px-2 py-1 border">Profile</th>
                <th className="px-2 py-1 border">Active</th>
                <th className="px-2 py-1 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it._id}>
                  <td className="px-2 py-1 border">{it.serviceType}</td>
                  <td className="px-2 py-1 border">{it.serviceType === 'Tickets' ? (theatres.find(t=> String(t._id)===String(it.serviceId))?.name || it.serviceId) : it.serviceId}</td>
                  <td className="px-2 py-1 border">{it.profileCode}</td>
                  <td className="px-2 py-1 border">{it.active? 'Yes':'No'}</td>
                  <td className="px-2 py-1 border flex gap-2">
                    <button className="px-2 py-1 bg-gray-200 rounded" onClick={()=>startEdit(it)}>Edit</button>
                    <button className="px-2 py-1 bg-red-600 text-white rounded" onClick={async()=>{ if(window.confirm('Delete account?')) { await GatewayV2Services.deleteGatewayAccount(it._id); load(); } }}>Delete</button>
                  </td>
                </tr>
              ))}
              {items.length===0 && !loading && (
                <tr><td className="px-2 py-2 text-gray-500" colSpan={5}>No accounts found</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
